import { Component, computed, DestroyRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { AddressModel, CreateAddressRequest } from '../../../../core/models/data/address.model';
import { AddressTypes } from '../../../../core/enums/data.enums';
import { OrganizationService } from '../../../../core/services/organization.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { OrganizationAddressForm } from '../../../../forms/organization-address/organization-address.form';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { GenericMessagingConstants, OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
  selector: 'ms-organization-address-form-modal',
  standalone: true,
  imports: [FaDuotoneIconComponent, OrganizationAddressForm, EscCloseDirective, ConfirmationModalComponent],
  templateUrl: './organization-address-form-modal.component.html',
  styleUrl: './organization-address-form-modal.component.scss'
})
export class OrganizationAddressFormModalComponent extends BaseFormComponent implements CanComponentDeactivate {

  private organizationService = inject(OrganizationService);
  private bootstrapService = inject(BootstrapService);
  private dirtyFormRegistry = inject(DirtyFormRegistryService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public organization = input<OrganizationModel | undefined>(undefined);
  public addressSaved = output<OrganizationModel>();
  public addressSaveFailed = output<string | undefined>();

  @ViewChild(OrganizationAddressForm) addressFormRef!: OrganizationAddressForm;
  @ViewChild('addressSaveConfirmationModalRef') saveConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('addressLeaveConfirmationModalRef') leaveConfirmationModalRef!: ConfirmationModalComponent;

  public AddressTypes = AddressTypes;
  public selectedType = signal<AddressTypes | undefined>(undefined);
  public isSaving = signal<boolean>(false);

  private modalSelector = '#organizationAddressFormModal';
  private saveConfirmationModalSelector = '#organizationAddressSaveConfirmationModal';
  private leaveConfirmationModalSelector = '#organizationAddressLeaveConfirmationModal';
  private pendingRequest: CreateAddressRequest | null = null;

  public existingByType = computed<Map<AddressTypes, AddressModel>>(() => {
    const map = new Map<AddressTypes, AddressModel>();
    const addresses = this.organization()?.addresses ?? [];
    for (const address of addresses) {
      map.set(address.addressType, address);
    }
    return map;
  });

  public selectedAddress = computed<AddressModel | undefined>(() => {
    const type = this.selectedType();
    return type ? this.existingByType().get(type) : undefined;
  });

  public openModal(): void {
    this.selectedType.set(undefined);
    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Show);
  }

  public selectType(type: AddressTypes): void {
    this.selectedType.set(type);
  }

  public backToTypeSelection(): void {
    this.selectedType.set(undefined);
  }

  public onCancel(): void {
    this.selectedType.set(undefined);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
    this.dirtyFormRegistry.unregister(this);
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.addressFormRef?.addressForm.dirty ?? false, () => {
      this.leaveConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.leaveConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
      this.bootstrapService.manageModal(this.leaveConfirmationModalSelector, ModalActions.Show);
    });
  }

  public onLeaveConfirmed(): void {
    this.bootstrapService.manageModal(this.leaveConfirmationModalSelector, ModalActions.Hide);
    this.selectedType.set(undefined);
    this.dirtyFormRegistry.unregister(this);
    this.resolvePendingLeave(true);
  }

  public onLeaveCancelled(): void {
    this.bootstrapService.manageModal(this.leaveConfirmationModalSelector, ModalActions.Hide);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Show);
    this.resolvePendingLeave(false);
  }

  public hasAddressOfType(type: AddressTypes): boolean {
    return this.existingByType().has(type);
  }

  public addressTypeIcon(type: AddressTypes): string {
    switch (type) {
      case AddressTypes.Billing: return 'file-invoice-dollar';
      case AddressTypes.Mailing: return 'envelope';
      case AddressTypes.Physical: return 'location-dot';
    }
  }

  public addressTypeDescription(type: AddressTypes): string {
    switch (type) {
      case AddressTypes.Billing: return 'Where invoices and billing correspondence are addressed.';
      case AddressTypes.Mailing: return 'Where general mail and notices are delivered.';
      case AddressTypes.Physical: return 'The physical location of the organization.';
    }
  }

  public onAddressSubmitted(request: CreateAddressRequest): void {
    if (this.selectedAddress()) {
      this.pendingRequest = request;
      const message = OrganizationsMessagingConstants.ORGANIZATIONS_ADDRESS_SAVE_CONFIRMATION()
        .replace('{addressType}', String(this.selectedType()));

      this.saveConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.saveConfirmationModalRef.message.set(message);

      this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
      this.bootstrapService.manageModal(this.saveConfirmationModalSelector, ModalActions.Show);
    } else {
      void this.submitAddress(request);
    }
  }

  public async editConfirmationModalConfirmed(): Promise<void> {
    this.bootstrapService.manageModal(this.saveConfirmationModalSelector, ModalActions.Hide);
    const request = this.pendingRequest;
    this.pendingRequest = null;
    if (request) {
      await this.submitAddress(request);
    }
  }

  public editConfirmationModalCancelled(): void {
    this.pendingRequest = null;
    this.bootstrapService.manageModal(this.saveConfirmationModalSelector, ModalActions.Hide);
    this.bootstrapService.manageModal(this.modalSelector, ModalActions.Show);
  }

  private async submitAddress(request: CreateAddressRequest): Promise<void> {
    const organizationId = this.organization()?.organizationId;
    if (!organizationId) return;

    this.isSaving.set(true);
    if (this.addressFormRef) {
      this.addressFormRef.isSubmitting = true;
    }

    try {
      const updatedAddresses: CreateAddressRequest[] = [];
      for (const [type, existing] of this.existingByType()) {
        if (type === request.type) continue;
        updatedAddresses.push(this.toRequest(existing));
      }
      updatedAddresses.push(request);

      const result = await this.organizationService.updateOrganizationAddresses(organizationId, { addresses: updatedAddresses });

      this.bootstrapService.manageModal(this.modalSelector, ModalActions.Hide);
      this.selectedType.set(undefined);
      this.dirtyFormRegistry.unregister(this);

      if (result.success && result.data) {
        this.addressSaved.emit(result.data);
      } else {
        this.addressSaveFailed.emit(result.message);
      }
    } finally {
      this.isSaving.set(false);
      if (this.addressFormRef) {
        this.addressFormRef.isSubmitting = false;
      }
    }
  }

  private toRequest(address: AddressModel): CreateAddressRequest {
    return {
      type: address.addressType,
      primaryAddress: address.primaryAddress,
      secondaryAddress: address.secondaryAddress,
      city: address.city,
      stateProvinceId: address.stateProvince.stateProvinceId,
      primaryZip: address.primaryZip,
      secondaryZip: address.secondaryZip
    };
  }
}
